/**
 * Privacy policy.
 *
 * Written against what the code does rather than against a template. Every
 * claim here was checked in the handlers: the counters that carry no visitor
 * identifier, the two browser-storage keys, the fields that reach Stripe, the
 * records that survive an account deletion, and the one external font request
 * the stylesheet makes. Nothing is asserted that the product does not do —
 * which cuts both ways, so the retention gaps are named rather than smoothed.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { LISTING_LIFETIME_DAYS } from '@bidtohire/shared';

import { PolicyPage, type PolicySection } from './policy-shared';

export const PRIVACY_EFFECTIVE = '21 August 2026';

const SECTIONS: readonly PolicySection[] = [
  {
    id: 'scope',
    title: 'What this covers',
    body: (
      <>
        <p>
          This policy describes what the operator of bidtohire.me (“we”, “us”) collects when you use
          the site, why we hold it, who else handles it, and how long it stays. It covers the website
          and the accounts, listings and payments made through it.
        </p>
        <p>
          Browsing the boards requires no account and asks nothing of you. Almost everything below
          concerns companies that post listings, because that is where the site collects anything at
          all.
        </p>
      </>
    ),
  },
  {
    id: 'collect',
    title: 'What we collect',
    body: (
      <>
        <p>Only these, and only for the reasons in the next section.</p>
        <ul>
          <li>
            <strong>Account details.</strong> The email address you register with, and the fact that
            it has been confirmed. Sign-in is handled by Amazon Cognito, which holds the address and
            the password; we never receive or store your password.
          </li>
          <li>
            <strong>Company profile.</strong> Whatever you put in it — company name, website and
            careers links, a description, location, size, funding stage, industry, social links, an
            optional billing contact address, and a logo image you upload. This is public, apart from
            the billing contact address, which is not shown on the site.
          </li>
          <li>
            <strong>Listing content.</strong> The role, its category and specialism, description,
            salary range, locations, employment and workplace type, seniority, skills, benefits, and
            the link out to your own posting. Listings are public once published.
          </li>
          <li>
            <strong>Payment records.</strong> For each payment: the amount, the currency, the
            resulting total, the time, and Stripe’s identifiers for the session and the payment. See
            section 05 — card numbers are not among them.
          </li>
          <li>
            <strong>Feedback submissions.</strong> The message you send through the feedback form,
            the page you sent it from, the email address you optionally leave, and — to keep the form
            from being used as a mail relay — the IP address and browser user-agent string it arrived
            with.
          </li>
          <li>
            <strong>Counts.</strong> How many times a listing has been viewed and how many people
            have clicked through to the employer’s posting. See section 06.
          </li>
          <li>
            <strong>Application logs.</strong> Our servers record what they did and what went wrong —
            listing and company identifiers, and error details. They do not record visitors’ IP
            addresses. We do not enable web access logs on the site or the API.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'why',
    title: 'Why we use it',
    body: (
      <>
        <p>
          Account details, profile, listing content and payment records are used to run the service
          you asked for: to sign you in, to publish and rank what you have paid to publish, to show
          you your listings and their positions, and to take payment. There is no way to provide the
          service without them.
        </p>
        <p>
          The IP address and user-agent on a feedback submission, and our application logs, are held
          because we have a genuine interest in keeping the site working and in not letting an open
          form become a spam relay. Counts exist because employers need to see whether what they paid
          for worked.
        </p>
        <p>
          We keep payment records because financial and tax rules require a business to be able to
          account for money it has taken.
        </p>
        <p>
          We do not use any of it to build a profile of you, to target advertising, or for any purpose
          you would not expect from the paragraphs above. If that ever changes, section 14 says how
          you will find out.
        </p>
      </>
    ),
  },
  {
    id: 'not',
    title: 'What we do not do',
    body: (
      <>
        <p>
          <strong>We do not sell personal data</strong>, share it for anyone else’s advertising, or
          exchange it for anything of value.
        </p>
        <p>
          There are no advertising trackers on this site, no analytics SDK, no tag manager, no
          pixels, and no session recording. Nothing on the page is watching you read it. We do not
          buy data about visitors from anyone else, and we do not attempt to identify the people who
          browse the boards.
        </p>
      </>
    ),
  },
  {
    id: 'payments',
    title: 'Payments',
    body: (
      <>
        <p>
          Payments are taken by <strong>Stripe</strong>, on Stripe’s own checkout page. Card numbers,
          expiry dates and security codes are entered there and are handled entirely by Stripe. They
          never pass through our site or our servers, and we could not store them if we wanted to.
        </p>
        <p>
          What we send Stripe to set up a payment is: the amount, the currency, the job title, the
          identifiers of the listing and the company, and — only if you have given us one — your
          billing contact email address so Stripe can send you a receipt. No name, address, or phone
          number is sent by us.
        </p>
        <p>
          What comes back and is stored on our side is the amount, the currency, the resulting total,
          the time, and Stripe’s session and payment identifiers. Stripe is a separate controller of
          the data it collects from you, under its own privacy policy.
        </p>
      </>
    ),
  },
  {
    id: 'counts',
    title: 'Views, clicks and measurement',
    body: (
      <>
        <p>
          Each listing carries two numbers: how many times its page has been viewed, and how many
          times someone has clicked through to the employer’s own posting.{' '}
          <strong>Both numbers are public.</strong> They are shown on the listing’s own page to
          anyone who opens it and are returned by our public API, so any visitor — a competing
          employer included — can read them. The company that owns the listing also sees them, and a
          total across its listings, in its dashboard.
        </p>
        <p>
          They are counters and nothing more. A view or a click adds one to an integer on the
          listing. <strong>No identifier of any kind is stored alongside it</strong> — no IP address,
          no cookie, no device fingerprint, no timestamp, no row per event. There is no record that
          could say who viewed a listing, because none is created. It follows that we cannot tell you
          what you personally have looked at, and neither can anyone who asks us.
        </p>
      </>
    ),
  },
  {
    id: 'storage',
    title: 'Cookies and browser storage',
    body: (
      <>
        <p>
          <strong>This site sets no cookies.</strong> Neither the website nor the API sends one, and
          there is no cookie banner because there is nothing to consent to.
        </p>
        <p>
          If you sign in, one entry is written to your browser’s local storage under the key{' '}
          <code>bidtohire.session</code>. It holds your sign-in tokens, the time they expire, and the
          email address you signed in with, and it exists so that a page reload does not sign you out.
          It stays on your device, is sent only to our own API as proof of who you are, and is deleted
          when you sign out. Clearing your browser’s site data removes it and signs you out.
        </p>
        <p>
          One other entry is written, and only when you place a bid: before your browser is handed
          over to Stripe, the identifier of the listing you are paying for is put in your browser’s{' '}
          <em>session</em> storage under the key <code>bidtohire.publishing</code>. Stripe returns
          you to your dashboard without saying which listing was paid for, and this is how that page
          knows. It holds that identifier and nothing else — no amount, no account, no personal
          detail — it is read and erased the moment the dashboard loads, and the browser discards it
          by itself when the tab closes. Placing a bid requires an account, so if you never sign in,
          nothing is written at all.
        </p>
      </>
    ),
  },
  {
    id: 'processors',
    title: 'Who else handles it',
    body: (
      <>
        <p>
          Three companies process data on our behalf or in the course of serving the site.
        </p>
        <ul>
          <li>
            <strong>Amazon Web Services</strong> hosts everything: the database, the servers, the
            content delivery network, the file storage that holds uploaded logos, sign-in through
            Amazon Cognito, and the mail service that delivers a feedback notification. AWS handles
            this data on our instructions.
          </li>
          <li>
            <strong>Stripe</strong> processes payments, as described in section 05.
          </li>
          <li>
            <strong>Google Fonts.</strong> The site’s typefaces are loaded from Google’s font
            service, so your browser makes a request to <code>fonts.googleapis.com</code> and{' '}
            <code>fonts.gstatic.com</code> when a page loads. Google receives the ordinary contents of
            that request — your IP address, your browser’s user-agent, and the page that asked for the
            font. We send Google nothing else. Google is the only company outside our own
          infrastructure that your browser contacts merely by viewing a page — the site’s other
          connections go to our own AWS endpoints: the API, the live bidding channel, and, when you
          upload a logo, file storage. Paying sends you to Stripe’s own checkout page, and clicking
          Apply sends you to the employer’s site, which is then subject to that site’s own policy.
          </li>
        </ul>
        <p>
          Beyond those, we disclose data only where the law requires it, where it is necessary to
          establish or defend a legal claim, or to protect the site and its users from harm. If the
          service is ever transferred to another operator, the data would move with it and this policy
          would continue to apply until you were told otherwise.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    title: 'How long we keep it',
    body: (
      <>
        <p>
          Your account, profile and listings are kept while your account is open. A published listing
          stops being shown {LISTING_LIFETIME_DAYS} days after publication or its last renewal, and is
          removed when you delete it or close your account.
        </p>
        <p>
          <strong>Payment records outlast the account.</strong> When a company closes its account, the
          ledger of what was paid — amounts, times and Stripe identifiers — is retained, because
          financial records must remain accountable after a customer leaves. It is no longer shown
          anywhere on the site.
        </p>
        <p>
          Feedback submissions, including the IP address and user-agent they arrived with, are kept as
          the record of what was reported; they are not linked to an account and are not removed by
          closing one. The hourly counter that limits the form expires by itself two hours after it is
          written. Application logs are kept for thirty days and then deleted automatically. Logo images
          uploaded to our file storage are deleted when the account that uploaded them is closed,
          including earlier logos that were replaced.
        </p>
      </>
    ),
  },
  {
    id: 'choices',
    title: 'Your choices',
    body: (
      <>
        <p>
          You can edit or delete any listing, and change anything in your company profile, from the
          dashboard at any time.
        </p>
        <p>
          <strong>You can close your account yourself</strong>, from the dashboard, without asking us.
          Doing so deletes your listings and their board positions, your company profile, and your
          sign-in. What remains afterwards is set out in section 09.
        </p>
        <p>
          Depending on where you live, you may have the right to ask for a copy of the personal data
          we hold about you, to have it corrected, to have it deleted, or to object to our use of it.
          Ask through the contact route in section 15 and we will answer. We will not treat you
          differently for asking. Where a request conflicts with keeping payment records, we will keep
          the ledger entry and delete the rest.
        </p>
        <p>
          The only email we send you is the address-confirmation code needed to open an account.
          Stripe sends payment receipts. We do not send marketing email, so there is no list to leave.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    title: 'Security',
    body: (
      <p>
        Traffic to the site and the API is encrypted in transit. Data is stored in Amazon Web Services
        with access limited to what each part of the service needs. Sign-in is handled by Amazon
        Cognito, which requires a password of at least twelve characters and a confirmed email
        address, and holds the password rather than us. Payment card details never reach our systems.
        No system is perfectly secure, and we cannot guarantee that one is; use a password you do not
        use anywhere else, and tell us if you think your account has been reached by someone else.
      </p>
    ),
  },
  {
    id: 'children',
    title: 'Children',
    body: (
      <p>
        The site is a hiring board for companies and is not directed to children. We do not knowingly
        collect personal data from anyone under 16. If you believe a child has given us personal data,
        tell us through the contact route in section 15 and we will delete it.
      </p>
    ),
  },
  {
    id: 'where',
    title: 'Where it is processed',
    body: (
      <p>
        The site is operated from the United States and its data is stored and processed there. If you
        use the site from another country, you are sending your data to the United States, where
        privacy law may differ from your own. Our processors in section 08 may also process data in
        other countries under their own arrangements.
      </p>
    ),
  },
  {
    id: 'changes',
    title: 'Changes to this policy',
    body: (
      <p>
        We may update this policy. The version on this page is the current one, and the date under the
        title is when it took effect. A change that materially affects what we collect or what we do
        with it will be reflected in that date, and continuing to use the site after it takes effect
        means the updated policy applies to you.
      </p>
    ),
  },
  {
    id: 'contact',
    title: 'Contact',
    body: (
      <p>
        Use the <strong>Report a bug</strong> link in the footer of any page. It opens a form that
        reaches the operator directly and needs no account; leave an email address if you want an
        answer. Use it for anything on this page, including a request to see, correct or delete what
        we hold. See also <Link to="/terms">the terms and conditions</Link>.
      </p>
    ),
  },
];

export function PrivacyPage(): ReactNode {
  return (
    <PolicyPage
      eyebrow="Legal"
      title="Privacy policy"
      lede={
        <>
          What bidtohire.me collects, why, and how long it stays. Browsing needs no account, and the
          site carries no advertising trackers or analytics.
        </>
      }
      effective={`In effect from ${PRIVACY_EFFECTIVE}.`}
      sections={SECTIONS}
    />
  );
}
